import type { ReactNode, SelectHTMLAttributes } from 'react'

/** The prototype's filter-bar `.form-select` (subscription-portal.html,
 * `.filter-field .form-select`): the native chevron replaced by its own,
 * inset 10px from the right like the text on the left, with room reserved
 * so the text never runs under it. 32px tall, 12.5px text, `6px 10px`
 * padding, 32px on the right.
 *
 * The chevron is an absolutely positioned SVG stroked in `currentColor` on
 * the tertiary text token, rather than the prototype's data-URI background
 * (whose stroke is a fixed grey), so it reads in both themes. It ignores the
 * pointer, so a click on it still opens the select.
 *
 * `variant="role"` is the prototype's in-row `.role-select` (Settings ›
 * Access's Permission cell, MS-997): `5px 28px 5px 10px` padding, 12px text,
 * its own height, and a 13px chevron inset 8px. `variant="form"` is the
 * modal `.form-select`: `8px 12px` padding, 13px text, the 14px chevron
 * inset 10px. */
const VARIANTS = {
  filter: {
    select: 'h-8 py-1.5 pl-2.5 pr-8 text-[12.5px]',
    chevron: { size: 14, className: 'right-2.5' },
  },
  role: {
    select: 'py-[5px] pl-2.5 pr-7 text-[12px]',
    chevron: { size: 13, className: 'right-2' },
  },
  form: {
    select: 'py-2 pl-3 pr-8 text-[13px]',
    chevron: { size: 14, className: 'right-2.5' },
  },
} as const

export function Select({
  children,
  className = '',
  variant = 'filter',
  ...props
}: Readonly<
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> & {
    children: ReactNode
    variant?: keyof typeof VARIANTS
  }
>) {
  const v = VARIANTS[variant]
  return (
    <div className={`relative ${className}`}>
      <select
        {...props}
        className={`w-full ${v.select} appearance-none cursor-pointer border border-[var(--border)] rounded-md bg-[var(--bg-input)] text-[var(--text-primary)] font-[inherit] transition-[border-color,box-shadow] focus:outline-none focus:border-[var(--border-focus)] focus:shadow-[0_0_0_3px_var(--accent-subtle)] disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {children}
      </select>
      <svg
        aria-hidden="true"
        data-testid="select-chevron"
        viewBox="0 0 24 24"
        width={v.chevron.size}
        height={v.chevron.size}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`pointer-events-none absolute ${v.chevron.className} top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  )
}
